import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api, useApiMutation, useApp, type App } from "../lib/api";
import { useCan } from "../lib/access";
import { DEFAULT_INSTALL_METHOD, INSTALL_METHOD_LABEL, INSTALL_METHOD_LANGUAGE, INSTALL_METHODS, installSnippets, type InstallMethod } from "@metrickle/schema/install";
import { track } from "../lib/track";
import { AppHeader, useAppId } from "../components/app-header";
import { ResearchSettingsCard } from "../components/research-settings";
import { Button, Card, CardHeader, CardSection, CodeBlock, Field, Input, Modal, TabBar, notifyError, toaster, useConfirm } from "../components/ui";

const INSTALL_TABS = INSTALL_METHODS.map((m) => ({ value: m, label: INSTALL_METHOD_LABEL[m] }));

export function AppSettings() {
  const appId = useAppId();
  const app = useApp(appId);
  const can = useCan(appId);
  const [tab, setTab] = useState<InstallMethod>("script");
  const navigate = useNavigate();

  useEffect(() => {
    if (app.data) setTab(DEFAULT_INSTALL_METHOD[app.data.platform]);
  }, [app.data]);

  if (!app.data) return <AppHeader title="Settings" />;
  const s = installSnippets(app.data, window.location.origin);

  return (
    <>
      <AppHeader title="Settings" />
      <div className="max-w-3xl space-y-4">
        <Card>
          <CardHeader
            title="Install"
            subtitle="Web captures pageviews, sources, web vitals, rage clicks and errors automatically. iOS, Android and Flutter capture screens, app opens, rage taps, u-turns and accessibility settings."
            actions={
              <TabBar
                aria-label="Install method"
                panelId="install-snippet"
                items={INSTALL_TABS}
                value={tab}
                onChange={(v) => setTab(v as InstallMethod)}
              />
            }
          />
          <div className="px-4 pb-4" id="install-snippet" role="tabpanel" aria-label={INSTALL_TABS.find((t) => t.value === tab)?.label}>
            <CodeBlock
              code={s[tab]}
              label={INSTALL_METHOD_LANGUAGE[tab]}
              language={INSTALL_METHOD_LANGUAGE[tab]}
              onCopy={() => track("install_snippet_copied", { method: tab, welcome: false })}
            />
          </div>
        </Card>
        <ResearchSettingsCard app={app.data} disabled={!can("research.settings")} />
        <GeneralSettings app={app.data} disabled={!can("apps.settings")} canRotate={can("apps.keys")} />
        {(can("data.erase") || can("apps.delete")) && (
          <DangerZone app={app.data} canErase={can("data.erase")} canDelete={can("apps.delete")} onDeleted={() => navigate("/")} />
        )}
      </div>
    </>
  );
}

function GeneralSettings({ app, disabled, canRotate }: { app: App; disabled: boolean; canRotate: boolean }) {
  const [name, setName] = useState(app.name);
  const [domains, setDomains] = useState(app.domains.join(", "));
  const [timezone, setTimezone] = useState(app.timezone);
  const update = useApiMutation(
    (body: Partial<Pick<App, "name" | "domains" | "timezone">>) => api<App>(`/apps/${app.id}`, { method: "PATCH", json: body }),
    [["app", app.id], ["apps"], ["workspaces"]],
  );
  const rotate = useApiMutation(() => api<App>(`/apps/${app.id}/rotate-key`, { method: "POST" }), [["app", app.id]]);
  const { confirm, dialog } = useConfirm();

  return (
    <Card>
      <CardHeader title="General" />
      <CardSection>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await update.mutateAsync({ name, domains: domains.split(/[\s,]+/).filter(Boolean), timezone });
              track("app_settings_saved");
              toaster.success({ title: "Saved" });
            } catch (err) {
              notifyError(err);
            }
          }}
        >
          <Field label="Name">
            <Input disabled={disabled} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          {app.platform === "web" && (
            <Field label="Allowed domains" hint="Events from other origins are rejected. Subdomains are included.">
              <Input disabled={disabled} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="example.com" />
            </Field>
          )}
          <Field label="Reporting timezone">
            <Input disabled={disabled} value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </Field>
          <Field label="Write key" hint={canRotate ? "Public by design: it can only send events. Rotate it if it's being abused." : "Public by design: it can only send events."}>
            <div className="flex gap-2">
              <Input readOnly value={app.writeKey} className="font-mono" onFocus={(e) => e.currentTarget.select()} />
              {canRotate && (
                <Button
                  type="button"
                  pending={rotate.isPending}
                  pendingLabel="Rotating…"
                  onClick={async () => {
                    if (!(await confirm({ title: "Rotate the write key?", description: "The old key stops working within 5 minutes, so update your install first.", confirmLabel: "Rotate" }))) return;
                    await rotate.mutateAsync(undefined).then(() => {
                      track("write_key_rotated");
                      toaster.success({ title: "Write key rotated", description: "The old key stops working within 5 minutes." });
                    }, notifyError);
                  }}
                >
                  Rotate
                </Button>
              )}
            </div>
          </Field>
          <Button variant="primary" disabled={disabled} pending={update.isPending} pendingLabel="Saving…">
            Save changes
          </Button>
        </form>
      </CardSection>
      {dialog}
    </Card>
  );
}

function DangerZone({ app, canErase, canDelete, onDeleted }: { app: App; canErase: boolean; canDelete: boolean; onDeleted: () => void }) {
  const [subject, setSubject] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const erase = useApiMutation((id: string) => api(`/apps/${app.id}/subjects/${encodeURIComponent(id)}`, { method: "DELETE" }), [["events", app.id]]);
  const del = useApiMutation(() => api(`/apps/${app.id}`, { method: "DELETE" }), [["apps"], ["portfolio"], ["workspaces"]]);
  const { confirm, dialog } = useConfirm();

  return (
    <Card>
      <CardHeader title="Privacy & data" />
      {canErase && (
        <CardSection>
          <p className="mb-3 text-sm text-ink-2">Erase all events for a visitor or user id (GDPR/CCPA right to erasure).</p>
          <form
            className="flex items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              const id = subject.trim();
              const ok = await confirm({
                title: `Erase all events for ${id}?`,
                description: "Every event recorded for them is deleted, and this can't be undone.",
                confirmLabel: "Erase",
              });
              if (!ok) return;
              try {
                await erase.mutateAsync(id);
                track("subject_erased");
                toaster.success({ title: "Erased", description: `All events for ${id} were deleted.` });
                setSubject("");
              } catch (err) {
                notifyError(err);
              }
            }}
          >
            <div className="min-w-0 flex-1">
              <Field label="Visitor or user id">
                <Input required className="font-mono" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </Field>
            </div>
            <Button pending={erase.isPending} pendingLabel="Erasing…">
              Erase
            </Button>
          </form>
        </CardSection>
      )}
      {canDelete && (
        <CardSection>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-ink">Delete app</div>
              <div className="text-xs text-ink-3">Stops ingestion and permanently deletes everything in it: events, replays, feedback, surveys, studies, tasks and settings.</div>
            </div>
            <Button variant="danger" onClick={() => setConfirmOpen(true)}>Delete</Button>
          </div>
          <Modal open={confirmOpen} onOpenChange={setConfirmOpen} title={`Delete ${app.name}?`} description="This cannot be undone. Type the app name to confirm.">
            <div className="space-y-3">
              <Field label={`Type “${app.name}” to confirm`}>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={app.name} autoComplete="off" spellCheck={false} />
              </Field>
              <Button
                variant="danger"
                className="w-full"
                disabled={typed !== app.name}
                pending={del.isPending}
                pendingLabel="Deleting…"
                onClick={async () => {
                  try {
                    await del.mutateAsync(undefined);
                    track("app_deleted");
                    onDeleted();
                  } catch (err) {
                    notifyError(err);
                  }
                }}
              >
                Delete permanently
              </Button>
            </div>
          </Modal>
        </CardSection>
      )}
      {dialog}
    </Card>
  );
}
